import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import { usersApi } from "../../api/users";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "pending", label: "Pendientes" },
  { value: "active", label: "Activos" },
  { value: "suspended", label: "Suspendidos" },
  { value: "invited", label: "Invitados" },
];

function statusChip(user) {
  if (user.status === "active") return <Chip size="small" color="success" label="Activo" />;
  if (user.status === "pending") return <Chip size="small" color="warning" label="Pendiente" />;
  if (user.status === "suspended") return <Chip size="small" color="error" label="Suspendido" />;
  return <Chip size="small" label={user.status || "—"} />;
}

export default function UsersAdminPage() {
  const [status, setStatus] = React.useState("pending");
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [savingId, setSavingId] = React.useState("");

  const loadUsers = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await usersApi.list({ status });
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message || "No se pudieron cargar los usuarios");
    } finally {
      setLoading(false);
    }
  }, [status]);

  React.useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const approve = async (user, globalRole = "client") => {
    try {
      setSavingId(user.id);
      setError("");
      await usersApi.approve(user.id, { globalRole });
      await loadUsers();
    } catch (err) {
      setError(err.message || "No se pudo aprobar el usuario");
    } finally {
      setSavingId("");
    }
  };

  const suspend = async (user) => {
    const ok = window.confirm(`¿Suspender a ${user.email}?`);
    if (!ok) return;
    try {
      setSavingId(user.id);
      setError("");
      await usersApi.suspend(user.id);
      await loadUsers();
    } catch (err) {
      setError(err.message || "No se pudo suspender el usuario");
    } finally {
      setSavingId("");
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
            Usuarios / clientes
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
            Acá aprobás cuentas, las convertís en clientes o las suspendés.
          </Typography>
        </Box>
        <TextField select size="small" label="Estado" value={status} onChange={(event) => setStatus(event.target.value)} sx={{ minWidth: 220, bgcolor: "#fff" }}>
          {STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {loading && <Alert severity="info">Cargando usuarios...</Alert>}

      {!loading && users.length === 0 && <Alert severity="info">No hay usuarios para este filtro.</Alert>}

      <Grid container spacing={2.5}>
        {users.map((user) => (
          <Grid item xs={12} md={6} xl={4} key={user.id}>
            <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)", height: "100%" }}>
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                    <Stack direction="row" spacing={1.5}>
                      <Box sx={{ width: 44, height: 44, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange }}>
                        <PeopleAltRoundedIcon />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontWeight: 950, color: BRAND.dark }}>{user.name}</Typography>
                        <Typography noWrap variant="body2" sx={{ color: "rgba(25,36,47,0.62)" }}>{user.email}</Typography>
                      </Box>
                    </Stack>
                    {statusChip(user)}
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip size="small" label={user.globalRole || "client"} />
                    <Chip size="small" color={user.emailVerified ? "success" : "warning"} label={user.emailVerified ? "Email verificado" : "Email sin verificar"} />
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {user.status !== "active" && !user.isSuperAdmin && (
                      <Button
                        startIcon={<CheckCircleRoundedIcon />}
                        disabled={savingId === user.id}
                        onClick={() => approve(user, "client")}
                        variant="contained"
                        sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                      >
                        Aprobar cliente
                      </Button>
                    )}
                    {user.status !== "active" && !user.isSuperAdmin && (
                      <Button
                        disabled={savingId === user.id}
                        onClick={() => approve(user, "staff")}
                        variant="outlined"
                        sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                      >
                        Aprobar staff
                      </Button>
                    )}
                    {user.status === "active" && !user.isSuperAdmin && (
                      <Button
                        startIcon={<BlockRoundedIcon />}
                        disabled={savingId === user.id}
                        onClick={() => suspend(user)}
                        color="error"
                        variant="outlined"
                        sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                      >
                        Suspender
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}
